import { createFileRoute, Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/changelog')({
  component: ChangelogPage,
  head: () => ({
    meta: [
      { title: 'Uptrack Changelog — Product Updates and New Features' },
      { name: 'description', content: 'See what\'s new in Uptrack: product updates, new monitoring features, alert channels, status page changes and other improvements.' },
      { property: 'og:title', content: 'Uptrack Changelog — Product Updates and New Features' },
      { property: 'og:image', content: 'https://uptrack.app/og/changelog.png' },
      { property: 'og:description', content: 'Product updates and new features for Uptrack uptime monitoring.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/changelog' },
    ],
  }),
})

type BadgeType = 'new' | 'improved' | 'fixed'

interface ChangelogEntry {
  text: string
  badge: BadgeType
}

interface ChangelogSection {
  date: string
  entries: ChangelogEntry[]
}

const badgeStyles: Record<BadgeType, string> = {
  new: 'bg-status-up-soft text-status-up border-status-up',
  improved: 'bg-muted text-primary border-border',
  fixed: 'bg-status-degraded-soft text-status-degraded border-status-degraded',
}

const changelog: ChangelogSection[] = [
  {
    date: 'November 2026',
    entries: [
      { text: '24 check locations in 12 areas for HTTP and keyword monitors, with a new US Central area (Dallas, Chicago) and new cities in Montréal, Milan and Delhi.', badge: 'new' },
      { text: 'Pick the areas that check each monitor: 6 areas on Pro, all 12 on Team and Business. You can also run a monitor on Cloudflare cities only, with no core probe.', badge: 'new' },
      { text: 'New alert rule for monitors with extra areas: several areas must fail before you get an alert. How it works: /docs/check-locations.', badge: 'improved' },
    ],
  },
  {
    date: 'October 11, 2026',
    entries: [
      { text: '22 check locations on six continents for HTTP and keyword monitors: Europe, North and South America, Asia, Australia and Africa. Pro picks 12 locations; Team and Business use all 22. Several locations must confirm a failure before you get an alert.' + (MONITOR_LOCATIONS.status ? ` ${MONITOR_LOCATIONS.status}` : ''), badge: 'new' },
      { text: 'Failures caused by bot protection now say so, e.g. "blocked by bot protection (Cloudflare challenge)", instead of only "HTTP 403".', badge: 'improved' },
    ],
  },
  {
    date: 'September 28, 2026',
    entries: [
      { text: 'Domain expiry monitors — RDAP lookup with a WHOIS fallback for TLDs without RDAP. Alerts a set number of days before the domain expires (default 30).', badge: 'new' },
      { text: 'DNS record change monitors — alert when the resolved A, AAAA, CNAME, MX, TXT, NS, or PTR record set changes, with the added and removed records listed.', badge: 'new' },
      { text: 'DNS blocklist monitors — check a host\'s IPv4 address against DNSBL zones such as SpamCop and Barracuda. Spamhaus may not answer these lookups.', badge: 'new' },
      { text: 'Sitemap monitors — fetch sitemap.xml and check a sample of up to 50 listed URLs for 4xx/5xx responses on each run.', badge: 'new' },
    ],
  },
  {
    date: 'June 14, 2026',
    entries: [
      { text: 'AI-authored browser checks — paste a URL and AI drafts a Playwright script you can review, edit, and run. Now free for any signed-in account.', badge: 'new' },
      { text: 'Ping (ICMP) monitors — check that a host is reachable, from every region. Available in the monitor type dropdown.', badge: 'new' },
      { text: 'Monitors-as-code CLI — the uptrack CLI is now a single static binary: define monitors in YAML and apply them from CI. Install with one command; see the guide at /guides/monitoring-as-code.', badge: 'new' },
      { text: 'Switch-from-Pingdom guide — export your Pingdom checks to CSV and bulk-import them at /guides/switch-from-pingdom.', badge: 'new' },
      { text: 'HTTP "Test now" runs a real server-side HTTP check (status + response time) instead of the browser probe; clearer rate-limit messages.', badge: 'fixed' },
    ],
  },
  {
    date: 'June 12, 2026',
    entries: [
      { text: 'Manage Uptrack from Claude or ChatGPT — a hosted MCP server you connect once (OAuth) to create monitors, check incidents, and run browser checks in plain language. New AI & MCP page at /ai.', badge: 'new' },
      { text: 'Real-browser checks — load your pages in a real headless browser for a screenshot, Core Web Vitals (LCP, CLS, FCP, TTFB), and visual-change detection', badge: 'new' },
      { text: 'Free browser-check tool — paste any URL for an instant screenshot plus Core Web Vitals, no signup, at /tools/browser-check', badge: 'new' },
      { text: 'Import from UptimeRobot, Pingdom, or any CSV — drag-and-drop bulk import from the Monitors page', badge: 'new' },
      { text: 'Early-warning degraded alerts — opt in per channel to hear when a monitor is struggling, before it is fully down', badge: 'new' },
    ],
  },
  {
    date: 'April 5, 2026',
    entries: [
      { text: 'Multi-region check consensus — checks from 3 continents must agree before alerting, zero false alerts', badge: 'new' },
      { text: 'New pricing — Free: 10 monitors at 30s. Pro: 50 all at 30s. Team: 200. Business: 1,000.', badge: 'improved' },
      { text: 'All alert channels free — Email, Slack, Discord, Telegram, Teams, Webhooks, Mattermost on every plan', badge: 'improved' },
      { text: 'GenServer-per-monitor architecture — persistent connections, 100K+ monitors per node', badge: 'improved' },
      { text: 'Magic link authentication — passwordless login, no password needed', badge: 'new' },
      { text: 'Claude Connector — OAuth 2.0 MCP server for AI-powered monitoring management', badge: 'new' },
      { text: '24 comparison pages — vs UptimeRobot, Datadog, Uptime Kuma, and 21 more', badge: 'new' },
      { text: '72,000 disposable email domains blocked', badge: 'improved' },
    ],
  },
  {
    date: 'April 1, 2026',
    entries: [
      { text: 'Response body assertions — JSONPath, regex, header checks, status code, and contains matching', badge: 'new' },
      { text: 'Slack slash commands: /uptrack status, /uptrack list, /uptrack help', badge: 'new' },
      { text: '@uptrack-app/cli published to npm', badge: 'new' },
      { text: '@uptrack-app/mcp published to npm — AI assistant integration for monitoring', badge: 'new' },
      { text: 'Homepage redesign with product screenshots', badge: 'improved' },
      { text: 'Mega menu navigation with full mobile support', badge: 'improved' },
      { text: '5 blog posts and 6 guides published', badge: 'new' },
      { text: 'RSS feed available at /rss.xml', badge: 'new' },
    ],
  },
  {
    date: 'March 29, 2026',
    entries: [
      { text: 'Competitive pricing — Free plan includes 5 monitors at 30s', badge: 'improved' },
      { text: 'Business plan: 625 monitors at 30s intervals', badge: 'new' },
      { text: 'Paddle billing integration live', badge: 'new' },
      { text: 'Annual billing with up to 40% savings', badge: 'new' },
    ],
  },
  {
    date: 'March 2026',
    entries: [
      { text: 'Public status pages with uptime bars', badge: 'new' },
      { text: 'SSL certificate monitoring', badge: 'new' },
      { text: 'DNS monitoring — A, AAAA, CNAME, MX, TXT record checks', badge: 'new' },
      { text: 'Cron / heartbeat monitoring', badge: 'new' },
      { text: 'Two-factor authentication (TOTP) with backup codes', badge: 'new' },
      { text: 'SSO / SAML support (Business plan)', badge: 'new' },
      { text: 'Audit logs on all plans', badge: 'new' },
      { text: 'Custom email sender (Business plan)', badge: 'new' },
    ],
  },
]

function ChangelogPage() {
  return (
    <>

      <div className="mx-auto max-w-3xl px-4 py-16 sm:py-24">
        <div className="mb-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            &larr; Back to home
          </Link>
        </div>

        <h1 className="mb-2 text-4xl font-semibold tracking-tight">Changelog</h1>
        <p className="mb-12 text-lg text-muted-foreground">
          New features, improvements, and fixes.
        </p>

        <div className="space-y-12">
          {changelog.map((section) => (
            <section key={section.date} className="relative">
              {/* Date header */}
              <div className="mb-6 flex items-center gap-4">
                <h2 className="text-lg font-semibold text-foreground whitespace-nowrap">
                  {section.date}
                </h2>
                <div className="h-px flex-1 bg-border" />
              </div>

              {/* Entries */}
              <ul className="space-y-3 pl-1">
                {section.entries.map((entry, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Badge
                      variant="outline"
                      className={`mt-0.5 shrink-0 text-xs uppercase tracking-wide ${badgeStyles[entry.badge]}`}
                    >
                      {entry.badge}
                    </Badge>
                    <span className="text-[15px] text-muted-foreground leading-relaxed">
                      {entry.text}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      </>
  )
}
